<script>
    import {Tween} from 'svelte/motion';
    import {cubicOut} from 'svelte/easing';

    import {ElevationRideTypes, PaceRideTypes, PaceProfiles, DurationEstimationModes} from '@js/components/modules/constants';
    import {RIDE_TYPE_ICON} from '@js/components/themes/icons';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';
    import StorageService from '@js/modules/storage';
    import {debounce} from '@js/modules/utils';
    import {estimateRideDuration, saveUserPaceProfile, getDurationEstimationMode} from '@js/actions/mapActions';
    import AnalyticsService from '@js/modules/analyticsService';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import Icon from '@js/components/themes/Icon.svelte';
    import Spinner from '@js/components/themes/Spinner.svelte';

    const RIDE_TYPE_STORAGE_KEY = 'MapEstimateRideType';

    const {
        estimateDuration = false,
        addBreakTime = false
    } = $props();

    const totalDurationSeconds = $derived(
        (Number($mapStore.currentDuration) || 0) + (addBreakTime ? (Number($mapStore.currentBreakTime) || 0) : 0)
    );

    let _previousDistance = mapStore.currentDistance;
    let _previousRideType = mapStore.currentRideType;
    let _hasAutoEstimated = false;

    let isDropdownOpen = $state(false);
    let isEstimating = $state(false);
    let hasEstimated = $state(false);

    const needsEstimation = $derived(estimateDuration && !$mapStore.currentDuration);

    const distanceProgress = new Tween(Number(mapStore.currentDistance) || 0, {
        duration: 800,
        easing: cubicOut
    });

    const durationProgress = new Tween(Number(mapStore.currentDuration) || 0, {
        duration: 800,
        easing: cubicOut
    });

    let outlineDataEl = $state(null);

    const clickOutside = (element, callbackFunction) => {
        function onClick(event) {
            if (!element || !outlineDataEl) {
                return;
            }

            if (!element.contains(event.target) && !outlineDataEl.contains(event.target)) {
                callbackFunction();
            }
        }

        document.body.addEventListener('click', onClick);

        return {
            update(newCallbackFunction) {
                callbackFunction = newCallbackFunction;
            },
            destroy() {
                document.body.removeEventListener('click', onClick);
            }
        };
    };

    const _closeDropdown = () => {
        isDropdownOpen = false;
    };

    const positionDropdown = (node) => {
        const margin = 8;
        const container = node.parentElement;

        if (container) {
            const {
                top,
                bottom
            } = container.getBoundingClientRect();
            const spaceBelow = window.innerHeight - bottom;
            const spaceAbove = top;

            if (spaceBelow < node.offsetHeight + margin && spaceAbove > spaceBelow) {
                node.classList.remove('top-full', 'mt-2');
                node.classList.add('bottom-full', 'mb-2');
            }
        }
    };

    const _handleEstimateClick = () => {
        if (!$mapStore.currentDistance) {
            return;
        }

        isDropdownOpen = !isDropdownOpen;
    };

    const _runEstimation = (rideType) => {
        if (!mapStore.map || isEstimating) {
            return;
        }

        isEstimating = true;
        hasEstimated = true;
        mapStore.currentRideType = rideType;

        AnalyticsService.trackMapComputeDuration(rideType);

        estimateRideDuration({
            distance: mapStore.currentDistance,
            rideType
        })
            .then((result) => {
                if (!result) {
                    return;
                }

                mapStore.currentDuration = result.duration;
                mapStore.durationComputed = false;

                mapStore.currentSpeed = Computation.computeSpeed(mapStore.currentDistance, result.chronoDuration);
                mapStore.speedComputed = true;

                const positiveElevation = ElevationRideTypes.includes(rideType)
                    ? (mapStore.elevationApsis?.positiveElevation || 0)
                    : 0;
                const weight = window.currentUserWeight || StorageService.getItem('MAPPLANNER-userWeight') || 0;
                mapStore.currentCalories = Computation.estimateCalories(rideType, result.duration, mapStore.currentDistance, positiveElevation, weight);

                StorageService.setItem(RIDE_TYPE_STORAGE_KEY, rideType);
            })
            .finally(() => {
                isEstimating = false;
            });
    };

    const _handleRideTypeSelect = (rideType) => {
        isDropdownOpen = false;
        _runEstimation(rideType);
    };

    const _handleChangePace = (paceProfile) => {
        isDropdownOpen = false;

        if (isEstimating || mapStore.currentPaceProfile === paceProfile) {
            return;
        }

        mapStore.currentPaceProfile = paceProfile;
        AnalyticsService.trackPaceProfileSelected(paceProfile, mapStore.currentRideType);
        saveUserPaceProfile(paceProfile);

        _runEstimation(mapStore.currentRideType);
    };

    const _debouncedEstimate = debounce(() => _runEstimation(mapStore.currentRideType), 500);

    const _recomputeFromCache = (rideType, paceProfile, coordinateElevations, distance) => {
        if (!estimateDuration || !distance || !coordinateElevations?.length || mapStore.map?.hasTimeData()) {
            return;
        }

        const elevationByDistance = coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]);
        const duration = Computation.estimateServerDuration(rideType, distance, elevationByDistance, {paceProfile});
        if (!duration) {
            return;
        }

        hasEstimated = true;
        mapStore.currentDuration = duration;
        mapStore.durationComputed = false;
        mapStore.currentSpeed = Computation.computeSpeed(distance, duration, {formatted: false});
        mapStore.speedComputed = true;

        const positiveElevation = ElevationRideTypes.includes(rideType) ? (mapStore.elevationApsis?.positiveElevation || 0) : 0;
        const weight = window.currentUserWeight || StorageService.getItem('MAPPLANNER-userWeight') || 0;
        mapStore.currentCalories = Computation.estimateCalories(rideType, duration, distance, positiveElevation, weight);
    };

    $effect(() => {
        distanceProgress.target = Number($mapStore.currentDistance) || 0;
    });
    $effect(() => {
        durationProgress.target = totalDurationSeconds;

        if (estimateDuration && $mapStore.currentDuration && !hasEstimated) {
            hasEstimated = true;
        }
    });

    let _lastInputs = null;
    $effect(() => {
        const coordinateElevations = $mapStore.coordinateElevations;
        const rideType = $mapStore.currentRideType;
        const paceProfile = $mapStore.currentPaceProfile;
        const distance = $mapStore.currentDistance;

        if (getDurationEstimationMode() !== DurationEstimationModes.CLIENT) {
            return;
        }

        if (_lastInputs
            && _lastInputs.coordinateElevations === coordinateElevations
            && _lastInputs.rideType === rideType
            && _lastInputs.paceProfile === paceProfile
            && _lastInputs.distance === distance) {
            return;
        }
        _lastInputs = {coordinateElevations, rideType, paceProfile, distance};

        _recomputeFromCache(rideType, paceProfile, coordinateElevations, distance);
    });

    // Server mode only: the legacy async triggers (auto-estimate, ride-type change, debounced redraw).
    $effect(() => {
        if (getDurationEstimationMode() !== DurationEstimationModes.SERVER) {
            return;
        }
        if (_hasAutoEstimated || !estimateDuration || !$mapStore.currentDistance || !$mapStore.map || $mapStore.currentDuration || $mapStore.map.hasTimeData()) {
            return;
        }

        _hasAutoEstimated = true;
        _handleRideTypeSelect($mapStore.currentRideType);
    });

    $effect(() => {
        const rideType = $mapStore.currentRideType;

        if (getDurationEstimationMode() !== DurationEstimationModes.SERVER || rideType === _previousRideType) {
            return;
        }
        _previousRideType = rideType;

        if (estimateDuration && $mapStore.currentDistance && $mapStore.map && !$mapStore.map.hasTimeData() && !isEstimating) {
            _handleRideTypeSelect(rideType);
        }
    });

    $effect(() => {
        const distance = $mapStore.currentDistance;

        if (getDurationEstimationMode() !== DurationEstimationModes.SERVER || distance === _previousDistance) {
            return;
        }
        _previousDistance = distance;

        if (hasEstimated && distance && !$mapStore.map?.hasTimeData()) {
            _debouncedEstimate();
        }
    });

</script>

<div class="map-data-outline">
    <div class="outline-container">
        <div class="outline-data">
            <div class="outline-value"
                 class:outline-value-on={$mapStore.currentDistance}>
                {Computation.formatDistance(distanceProgress.current, {
                    unit: $userStore.unit,
                    smartUnit: $userStore.unit !== 'mi',
                    kUnit: $userStore.unit === 'mi'
                })}
            </div>

            <div class="outline-name">
                {I18n.t('js.maps.outline.distance')}
            </div>
        </div>

        <div class="outline-data relative" bind:this={outlineDataEl}>
            {#if isEstimating}
                <div class="outline-value">
                    <Spinner size="4" color="primary"/>
                </div>
                <div class="outline-name">
                    {I18n.t('js.maps.outline.duration')}
                </div>
            {:else if needsEstimation}
                <div class="outline-duration-estimate relative flex flex-col items-center">
                    <button type="button"
                            class="outline-value inline-flex items-center gap-1 text-xs italic text-primary-600 hover:text-primary-800 cursor-pointer"
                            onclick={_handleEstimateClick}>
                        <svg class="w-4 h-4"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 24 24"
                             stroke-width="2"
                             stroke="currentColor">
                            <path stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                        </svg>
                        {I18n.t('js.maps.outline.estimate')}
                    </button>

                    <div class="outline-name text-primary-600">
                        {I18n.t('js.maps.outline.duration')}
                    </div>

                    {#if isDropdownOpen}
                        <div class="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]"
                             use:clickOutside={_closeDropdown}
                             use:positionDropdown>
                            <div class="px-3 py-1.5 text-xs text-gray-400 italic">
                                {I18n.t('js.ride.map.estimate_duration.menu')}
                            </div>
                            {#each Object.keys(RIDE_TYPE_ICON) as rideType (rideType)}
                                <button type="button"
                                        class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        class:bg-primary-100={$mapStore.currentRideType === rideType}
                                        onclick={() => _handleRideTypeSelect(rideType)}>
                                    <Icon class="h-4 w-4"
                                          icon={RIDE_TYPE_ICON[rideType]}/>
                                    {I18n.t(`js.ride.enums.ride_type.${rideType}`)}
                                </button>
                            {/each}

                            <div class="px-3 py-1.5 mt-1 border-t border-gray-100 text-xs text-gray-400 italic">
                                {I18n.t('js.ride.map.estimate_duration.pace_menu')}
                            </div>
                            {#each PaceProfiles as paceProfile (paceProfile)}
                                <button type="button"
                                        class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        class:bg-primary-100={$mapStore.currentPaceProfile === paceProfile}
                                        onclick={() => _handleChangePace(paceProfile)}>
                                    {I18n.t(`js.ride.enums.pace_profile.${paceProfile}`)}
                                </button>
                            {/each}
                        </div>
                    {/if}
                </div>
            {:else}
                <button type="button"
                        class="outline-duration-estimate relative flex flex-col items-center cursor-pointer"
                        onclick={estimateDuration ? _handleEstimateClick : undefined}>
                    <span class="outline-value inline-flex items-center gap-1">
                        {Computation.formatDuration(durationProgress.current, {withLeadingZero: false}) || '-'}

                        {#if estimateDuration && hasEstimated && RIDE_TYPE_ICON[$mapStore.currentRideType]}
                            <Icon class="h-4 w-4 text-gray-400"
                                  icon={RIDE_TYPE_ICON[$mapStore.currentRideType]}/>
                        {/if}
                    </span>

                    <span class="outline-name">
                        {I18n.t('js.maps.outline.duration')}
                    </span>

                    {#if addBreakTime && $mapStore.currentBreakTime}
                        <span class="outline-break-hint">
                            {I18n.t('js.maps.outline.break_included', {
                                duration: Computation.formatDuration($mapStore.currentBreakTime, {withLeadingZero: false})
                            })}
                        </span>
                    {/if}
                </button>

                {#if isDropdownOpen}
                    <div class="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]"
                         use:clickOutside={_closeDropdown}
                         use:positionDropdown>
                        <div class="px-3 py-1.5 text-xs text-gray-400 italic">
                            {I18n.t('js.ride.map.estimate_duration.menu')}
                        </div>
                        {#each Object.keys(RIDE_TYPE_ICON) as rideType (rideType)}
                            <button type="button"
                                    class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    class:bg-primary-100={$mapStore.currentRideType === rideType}
                                    onclick={() => _handleRideTypeSelect(rideType)}>
                                <Icon class="h-4 w-4"
                                      icon={RIDE_TYPE_ICON[rideType]}/>
                                {I18n.t(`js.ride.enums.ride_type.${rideType}`)}
                            </button>
                        {/each}

                        <div class="px-3 py-1.5 mt-1 border-t border-gray-100 text-xs text-gray-400 italic">
                            {I18n.t('js.ride.map.estimate_duration.pace_menu')}
                        </div>
                        {#each PaceProfiles as paceProfile (paceProfile)}
                            <button type="button"
                                    class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    class:bg-primary-100={$mapStore.currentPaceProfile === paceProfile}
                                    onclick={() => _handleChangePace(paceProfile)}>
                                {I18n.t(`js.ride.enums.pace_profile.${paceProfile}`)}
                            </button>
                        {/each}
                    </div>
                {/if}
            {/if}
        </div>

        <div class="flex items-center gap-2.5 md:gap-[22px]">
            {#if PaceRideTypes.includes($mapStore.currentRideType)}
                <div class="outline-data">
                    <div class="outline-value"
                         class:outline-value-on={$mapStore.currentSpeed}>
                        {Computation.formatPace($mapStore.currentSpeed, {unit: $userStore.unit}) || '-'}
                    </div>

                    <div class="outline-name">
                        {I18n.t('js.ride.map.summary.pace')}
                    </div>
                </div>
            {/if}

            {#if $mapStore.currentRideType !== 'hiking'}
                <div class="outline-data">
                    <div class="outline-value"
                         class:outline-value-on={$mapStore.currentSpeed}>
                        {Computation.formatSpeed($mapStore.currentSpeed, {
                            unit: $userStore.unit,
                            unitTarget: 'mph'
                        }) || '-'}
                    </div>

                    <div class="outline-name">
                        {I18n.t('js.ride.map.summary.speed')}
                    </div>
                </div>
            {/if}

            {#if ElevationRideTypes.includes($mapStore.currentRideType)}
                <div class="outline-data">
                    <div class="outline-value"
                         class:outline-value-on={$mapStore.elevationApsis?.positiveElevation}>
                        {Computation.formatDistance($mapStore.elevationApsis?.positiveElevation, {
                            smartUnit: false,
                            kUnit: false
                        }) || '-'}
                    </div>

                    <div class="outline-name">
                        {I18n.t('js.maps.outline.elevation_gain')}
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>
